import { FC, useState } from 'react';
import { Icon } from '@pypestream/design-system';

import { ResourceLibItem } from '../../../xstate/prostudio.xstate.types';
import { tableStore } from '../../../stores/table-store';

/**
 * A catalogued Resource Library entry, drawn as the prototype's `.lib-card`
 * (`prostudio-build-flow-layouts.html`): a drag grip, the kind's icon, the
 * name and node count on top, the description clamped to two lines with
 * "See more" to open it. Microagents sit on gray, action nodes on the action
 * wash. Clicking adds it; dragging places it on an agent.
 */
export const LibraryCard: FC<{
  item: ResourceLibItem;
  kind: 'microagent' | 'action';
  disabled?: boolean;
  dragEnabled?: boolean;
  onSelect: (item: ResourceLibItem) => void;
  /** A small tag after the name — "System" on a built-in action node. */
  tag?: string;
}> = ({ item, kind, disabled, dragEnabled, onSelect, tag }) => {
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const draggable = !!dragEnabled && !disabled && !!item.data;

  return (
    <div
      className={[
        'c-lib-card',
        kind === 'action' && 'c-lib-card--action',
        open && 'is-open',
        dragging && 'is-dragging',
        disabled && 'is-disabled',
      ]
        .filter(Boolean)
        .join(' ')}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      data-testid="bobcat-library-card"
      draggable={draggable}
      onClick={() => !disabled && onSelect(item)}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect(item);
        }
      }}
      onDragStart={
        draggable
          ? (e) => {
              e.dataTransfer.effectAllowed = 'copy';
              e.dataTransfer.setData('text/plain', item.name ?? '');
              setDragging(true);
              tableStore.setResourceLibDragData({
                data: item.data ?? [],
                name: item.name ?? 'Resource',
              });
            }
          : undefined
      }
      onDragEnd={
        draggable
          ? () => {
              setDragging(false);
              tableStore.setResourceLibDragData(null);
            }
          : undefined
      }
    >
      <div className="c-lib-card__head">
        {draggable && (
          <span className="c-lib-card__grip" aria-hidden="true">
            <Icon name="drag-indicator" size="xsmall" />
          </span>
        )}
        <Icon
          name={kind === 'action' ? 'action-node' : 'microagent'}
          size="small"
        />
        <span className="c-lib-card__name">{item.name}</span>
        {tag && <span className="c-lib-card__tag">{tag}</span>}
        {kind === 'microagent' && item.nodes ? (
          <>
            <span className="c-lib-card__count">{item.nodes}</span>
            <span className="c-lib-card__count-label">Nodes</span>
          </>
        ) : null}
      </div>
      {item.description ? (
        <>
          <p className="c-lib-card__desc">{item.description}</p>
          <button
            type="button"
            className="c-lib-card__more"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((current) => !current);
            }}
          >
            {open ? 'See less' : 'See more'}
          </button>
        </>
      ) : null}
    </div>
  );
};
